import { useQueryClient } from '@tanstack/react-query'
import {
  Button,
  Card,
  Col,
  Descriptions,
  Flex,
  Form,
  FormInstance,
  InputNumber,
  Modal,
  ModalProps,
  Radio,
  Row,
  Select
} from 'antd'
import dayjs from 'dayjs'
import { useState } from 'react'

import {
  useClassifierServiceGetInvestigationClassifierEndpoint,
  useClassifierServiceGetInvestigationTestTypeEndpoint,
  useInvestigationsServiceApproveInvestigationTestEndpoint,
  useInvestigationsServiceGetInvestigationTestEndpointKey,
  useInvestigationsServiceGetInvestigationTestsEndpointKey,
  useInvestigationsServiceGetListSpecimensEndpointKey,
  useInvestigationsServiceUpdateInvestigationTestResultEndpoint
} from 'src/api/queries'
import { ApiError, InvestigationTestResultDto } from 'src/api/requests'
import { message, SaveConfirm, Text } from 'src/components'
import { useLocales } from 'src/locales'
import { formErrorHandler } from 'src/utils'

/** What the approving doctor decided about a recorded result. */
type ConfirmResult = 'current' | 'different'

type ConfirmationValues = {
  confirmResult?: ConfirmResult
  confirmResultOptionId?: string
  confirmResultValueOptionId?: string
  confirmResultValue?: number
}

type Props = {
  testId: string
  results?: Array<InvestigationTestResultDto>
  onCancel: () => void
  onSuccess?: () => void
  /** The investigation type classifier id. When available, result/value
   *  options are read from it (same source AddResultModal uses to create the
   *  result), since the standalone test-type endpoint doesn't reliably
   *  hydrate nested value options. */
  typeId?: string
} & ModalProps

const CONFIRMATIONS = 'confirmations'

/** Approval of a single result: the doctor either confirms what the laboratory
 *  entered or replaces it before the test is sent on. The selectable options
 *  come from the result's own test type, so each row loads its own. */
const ConfirmResultFields = ({
  form,
  result,
  typeId
}: {
  form: FormInstance
  result: InvestigationTestResultDto
  typeId?: string
}) => {
  const { t, currentLang } = useLocales()

  const lang = currentLang.value
  const resultId = String(result.id)

  const confirmResult = Form.useWatch(
    [CONFIRMATIONS, resultId, 'confirmResult'],
    form
  )
  const confirmResultOptionId = Form.useWatch(
    [CONFIRMATIONS, resultId, 'confirmResultOptionId'],
    form
  )

  // Same source AddResultModal reads from when the result is created: the
  // standalone test-type-by-id endpoint doesn't reliably hydrate nested value
  // options, so the investigation type's own test types are preferred here.
  const { data: investigation } =
    useClassifierServiceGetInvestigationClassifierEndpoint(
      { id: String(typeId) },
      [typeId],
      { enabled: !!typeId && confirmResult === 'different' }
    )

  const { data: testType } =
    useClassifierServiceGetInvestigationTestTypeEndpoint(
      { id: String(result.testType?.id) },
      [result.testType?.id],
      {
        enabled:
          !typeId && !!result.testType?.id && confirmResult === 'different'
      }
    )

  const resultOptions =
    investigation?.testTypes?.find((item) => item.id === result.testType?.id)
      ?.resultOptions ??
    testType?.resultOptions ??
    []
  const valueOptions =
    resultOptions.find((option) => option.id === confirmResultOptionId)
      ?.valueOptions ?? []

  // A qualitative value belongs to the option it was picked under, so picking
  // another option clears it (rather than restoring the recorded one).
  const onResultOptionChange = () =>
    form.setFieldValue(
      [CONFIRMATIONS, resultId, 'confirmResultValueOptionId'],
      undefined
    )

  const recordedItems = [
    {
      key: 'result_option',
      label: t('tests_results.result'),
      children: result.resultOption?.translation?.[lang] ?? '-'
    },
    {
      key: 'result_value_option',
      label: t('specimens.result_option'),
      children: result.resultValueOption?.translation?.[lang] ?? '-'
    },
    {
      key: 'result_value',
      label: t('investigations.result_numeric_value'),
      children: result.resultValue ?? '-'
    },
    ...(result.laboratoryResultStatus
      ? [
          {
            key: 'device_result',
            label: t('tests_results.device_result'),
            children: result.laboratoryResultStatus
          }
        ]
      : [])
  ]

  return (
    <Card
      size="small"
      title={
        <Text weight={600}>
          {result.testType?.translation?.[lang] ?? t('tests_results.result')}
        </Text>
      }
    >
      <Descriptions
        size="small"
        column={{ xs: 1, sm: 2 }}
        items={recordedItems}
      />

      <Form.Item
        name={[CONFIRMATIONS, resultId, 'confirmResult']}
        label={t('tests_results.confirm_result')}
        style={{ marginTop: 12, marginBottom: 0 }}
      >
        <Radio.Group>
          <Radio value="current">
            {t('tests_results.confirm_result_current')}
          </Radio>
          <Radio value="different">
            {t('tests_results.confirm_result_different')}
          </Radio>
        </Radio.Group>
      </Form.Item>

      {confirmResult === 'different' && (
        <Row gutter={[16, 12]} style={{ marginTop: 12 }}>
          <Col span={12}>
            <Form.Item
              name={[CONFIRMATIONS, resultId, 'confirmResultOptionId']}
              label={t('tests_results.result')}
            >
              <Select
                allowClear
                placeholder={t('tests_results.result')}
                options={resultOptions.map((option) => ({
                  value: option.id,
                  label: option.translation?.[lang]
                }))}
                onChange={onResultOptionChange}
              />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              name={[CONFIRMATIONS, resultId, 'confirmResultValueOptionId']}
              label={t('specimens.result_option')}
            >
              <Select
                allowClear
                disabled={!confirmResultOptionId}
                placeholder={t('specimens.result_option')}
                options={valueOptions.map((option) => ({
                  value: option.id,
                  label: option.translation?.[lang]
                }))}
              />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              name={[CONFIRMATIONS, resultId, 'confirmResultValue']}
              label={t('investigations.result_numeric_value')}
            >
              <InputNumber
                className="full-width"
                placeholder={t('investigations.result_numeric_value')}
              />
            </Form.Item>
          </Col>
        </Row>
      )}
    </Card>
  )
}

/** Approval screen of a laboratory test: the doctor goes through every result,
 *  confirms it or enters a different one, and only then is the test sent to the
 *  requesting doctor. The approve endpoint carries no payload, so a replaced
 *  result is written through the result update endpoint first. */
export const ApproveTestModal = ({
  testId,
  results,
  onCancel,
  onSuccess,
  typeId,
  ...props
}: Props) => {
  const { t } = useLocales()
  const [form] = Form.useForm()
  const queryClient = useQueryClient()

  const [isSaving, setIsSaving] = useState(false)

  // Results without an id can't be updated, so they are only confirmed as they
  // are, together with the test.
  const editableResults = (results ?? []).filter((result) => !!result.id)

  const initialValues = {
    [CONFIRMATIONS]: Object.fromEntries(
      editableResults.map((result) => [
        String(result.id),
        {
          confirmResult: 'current',
          confirmResultOptionId: result.resultOption?.id ?? undefined,
          confirmResultValueOptionId: result.resultValueOption?.id ?? undefined,
          confirmResultValue: result.resultValue ?? undefined
        }
      ])
    )
  }

  const { mutateAsync: updateResult } =
    useInvestigationsServiceUpdateInvestigationTestResultEndpoint()

  const { mutateAsync: approveTest } =
    useInvestigationsServiceApproveInvestigationTestEndpoint()

  const invalidate = () =>
    [
      useInvestigationsServiceGetInvestigationTestsEndpointKey,
      useInvestigationsServiceGetInvestigationTestEndpointKey,
      useInvestigationsServiceGetListSpecimensEndpointKey
    ].forEach((key) => queryClient.invalidateQueries({ queryKey: [key] }))

  const onSubmit = async () => {
    const values = await form.validateFields()

    setIsSaving(true)

    try {
      // The corrections go in before the approval: once the test is approved
      // editing it again needs the approved-result permission.
      for (const result of editableResults) {
        const confirmation: ConfirmationValues =
          values?.[CONFIRMATIONS]?.[String(result.id)] ?? {}

        if (confirmation.confirmResult !== 'different') continue

        await updateResult({
          id: String(result.id),
          requestBody: {
            resultOptionId: confirmation.confirmResultOptionId || undefined,
            resultValueOptionId:
              confirmation.confirmResultValueOptionId || undefined,
            resultValue: confirmation.confirmResultValue ?? undefined,
            // The determination date stays the laboratory's; only the value is
            // being corrected here.
            resultDate: dayjs(result.resultDate).format()
          }
        })
      }

      await approveTest({ id: testId })

      message.success(t('investigations.successfully_send_result'))
      invalidate()
      onSuccess?.()
      onCancel()
    } catch (err) {
      formErrorHandler((err as ApiError).body?.errors)
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <Modal
      open
      centered
      width={800}
      footer={null}
      maskClosable={false}
      title={t('analysis.add_analysis.send_test_result')}
      onCancel={onCancel}
      styles={{ body: { maxHeight: '70vh', overflowY: 'auto' } }}
      {...props}
    >
      <Form
        form={form}
        layout="vertical"
        requiredMark={false}
        initialValues={initialValues}
      >
        <Flex vertical gap={16}>
          {editableResults.map((result) => (
            <ConfirmResultFields
              key={result.id}
              form={form}
              result={result}
              typeId={typeId}
            />
          ))}

          <Flex justify="center" gap={8}>
            <Button onClick={onCancel}>{t('common.cancel')}</Button>

            <SaveConfirm
              title={`${t('analysis.add_analysis.send_test_result')}?`}
              onConfirm={onSubmit}
              okButtonProps={{ loading: isSaving }}
            >
              <Button type="primary">
                {t('analysis.add_analysis.send_test_result')}
              </Button>
            </SaveConfirm>
          </Flex>
        </Flex>
      </Form>
    </Modal>
  )
}
